// ============================================
// ModalCerrarExpediente.js
// ============================================
// Modal generico para los 3 tipos de cierre: ganado / perdido / descartado.
// Carga el catalogo /api/oportunidades/catalogos/razones-cierre y filtra
// segun `tipo`. Si la razon es 'otro', la nota es obligatoria.
// Solo rol 'direccion' puede llegar aqui (lo controla FichaExpediente).

import React, { useEffect, useState } from 'react';
import {
  API_BASE_CLI, FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE,
  COLOR_OK, COLOR_PELIGRO, inputStyle, botonSecundario, botonPrimario, botonPeligro,
  authHeaders, ShellModal, ToastFlotante,
} from './helpersExpedientes';

const META = {
  ganado: {
    titulo: 'Cerrar como ganado',
    advertencia: 'Esta accion marcara la venta como concretada y la sumara a los reportes.',
    color: COLOR_OK,
    boton: 'Confirmar cierre ganado',
    endpoint: 'cerrar-ganado',
  },
  perdido: {
    titulo: 'Cerrar como perdido',
    advertencia: 'Esta accion registra que la venta se perdio. Afecta los reportes.',
    color: COLOR_PELIGRO,
    boton: 'Confirmar cierre perdido',
    endpoint: 'cerrar-perdido',
  },
  descartado: {
    titulo: 'Descartar expediente',
    advertencia: 'Descartar es para duplicados, errores o pruebas. NO afecta los reportes de ventas.',
    color: '#9ca3af',
    boton: 'Confirmar descarte',
    endpoint: 'descartar',
  },
};

export default function ModalCerrarExpediente({ tipo, expediente, onCerrar, onConfirmado, esCelular }) {
  const [razones, setRazones] = useState([]);
  const [cargandoCat, setCargandoCat] = useState(true);
  const [razon, setRazon] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const meta = META[tipo];

  useEffect(() => {
    let activo = true;
    fetch(`${API_BASE_CLI}/api/oportunidades/catalogos/razones-cierre`)
      .then(r => r.json())
      .then(j => {
        if (!activo) return;
        const lista = j?.razones?.[tipo === 'descartado' ? 'descarte' : (tipo === 'perdido' ? 'perdida' : 'ganado')] || [];
        setRazones(lista);
        setCargandoCat(false);
      })
      .catch(() => { if (activo) setCargandoCat(false); });
    return () => { activo = false; };
  }, [tipo]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enviando, onCerrar]);

  const necesitaNota = razon === 'otro';
  const valido = !!razon && (!necesitaNota || nota.trim().length > 0);

  const confirmar = async () => {
    if (!valido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}/${meta.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ razon, nota: nota.trim() || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para esta accion (solo direccion).');
        return;
      }
      if (r.status === 409) {
        setError(data?.mensaje || 'El expediente ya esta en otro estado de cierre.');
        return;
      }
      if (!r.ok || !data?.ok) {
        throw new Error(data?.error || `Error ${r.status}`);
      }
      setToast({ tipo: 'ok', msg: `Expediente cerrado como ${tipo}.` });
      setTimeout(() => onConfirmado(data.oportunidad), 1000);
    } catch (e) {
      setError(e.message || 'No se pudo cerrar el expediente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <ShellModal onClose={enviando ? () => {} : onCerrar} ancho={480} esCelular={esCelular}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>
              {meta.titulo}
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              {expediente.folio}
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={enviando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: enviando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 22, padding: 4, fontFamily: FF,
              opacity: enviando ? 0.5 : 1,
            }}
          >{'×'}</button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <div style={{
            background: meta.color + '14', color: meta.color,
            padding: '10px 12px', borderRadius: 8,
            fontSize: 12, fontWeight: 600, lineHeight: 1.5,
            border: `1px solid ${meta.color}33`,
          }}>{meta.advertencia}</div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>
              Razon <span style={{ color: COLOR_PELIGRO }}>*</span>
            </span>
            <select
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              disabled={cargandoCat || enviando}
              style={inputStyle}
            >
              <option value="">{cargandoCat ? 'Cargando razones...' : '-- Selecciona una razon --'}</option>
              {razones.map(r => (
                <option key={r} value={r}>{prettyRazon(r)}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>
              Nota {necesitaNota && <span style={{ color: COLOR_PELIGRO }}>*</span>}
              {!necesitaNota && <span style={{ color: COLOR_GRIS_CL, fontWeight: 500 }}> (opcional)</span>}
            </span>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder={necesitaNota ? 'Describe la razon especifica...' : 'Detalle adicional (opcional)'}
              rows={3}
              disabled={enviando}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: FF }}
            />
          </label>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10,
        }}>
          <button onClick={onCerrar} disabled={enviando} style={botonSecundario}>Cancelar</button>
          <button
            onClick={confirmar}
            disabled={!valido || enviando}
            style={{
              ...(tipo === 'ganado' ? botonPrimario : botonPeligro),
              ...(tipo === 'descartado' ? { background: '#6b7280' } : {}),
              opacity: (!valido || enviando) ? 0.5 : 1,
              cursor: (!valido || enviando) ? 'not-allowed' : 'pointer',
            }}
          >{enviando ? 'Procesando...' : meta.boton}</button>
        </div>
      </ShellModal>
      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </>
  );
}

// 'precio_alto' -> 'Precio alto'. 'no_respondio' -> 'No respondio'.
function prettyRazon(codigo) {
  if (codigo === 'otro') return 'Otro (requiere nota)';
  return codigo
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase());
}
